// @flow

import _ from 'lodash'
import { stripInvalid } from '../../../shared/amount-utils'
import { getCurrencyPrecision } from '../../../lib/utils/currencyUtils'
import { MGM_AMOUNT } from '../transfer/constants'

/**
 * Vanilla util operations needed by amount component.
 *
 */

// let's make this node safe, by wrapping these
const removeChild = (...args) =>
  document &&
  document.body &&
  document.body.removeChild &&
  document.body.removeChild(...args)
const appendChild = (...args) =>
  document &&
  document.body &&
  document.body.appendChild &&
  document.body.appendChild(...args)

const DEFAULT_FONT_SIZE = 42
const FONT_SIZE_MAPPING = {
  '0': { '8': 40 },
  '1': { '7': 38, '8': 36, '9': 32, '10': 30 },
  '2': { '7': 38, '8': 36, '9': 32, '10': 28 },
  '3': { '6': 36, '7': 32, '8': 28, '9': 26, '10': 24 },
  '4': { '6': 36, '7': 32, '8': 28, '9': 26, '10': 24 },
}
const JAPAN_NUMBER_MAPPING = {
  '０': '0',
  '１': '1',
  '２': '2',
  '３': '3',
  '４': '4',
  '５': '5',
  '６': '6',
  '７': '7',
  '８': '8',
  '９': '9',
}
/*******************************************
 * PRIVATE API
 *******************************************/

/**
 * Prepends the input digit amount with zeroes ("1" => "001")
 * @param {number} currencyPrecision - returns number of digits and decimal separator i.e.  2 for USD, 0 for JPY
 * @param {string} amount entered value i.e 12
 * @returns {string} formatted value with total number of zeroes prepended
 */
export function prependWithZeroes(currencyPrecision: number, amount: string) {
  const numOfZeroesToAdd = Math.max(0, currencyPrecision + 1 - amount.length)
  return '0'.repeat(numOfZeroesToAdd) + amount
}

/**
 * This is a utility function that will round the given number down
 * to the given precision. '42.678', 2 --> 42.67
 * Using toFixed will round up which allows users to type `9` and bump the number up :-/
 *
 * @param {string} separator The decimal separator
 * @param {string} numberString
 * @param {number} precision
 * @return {number} the rounded down number (with precision)
 */
export function roundDownWithPrecision(
  separator: CurrencySeparator,
  numberString: string,
  precision: number = 0
) {
  const parts = numberString.split(separator)
  let newNumberString = numberString
  if (parts.length > 1) {
    // means there is decimal part

    if (parts[0] === '') {
      parts[0] = '0' // add 0 to integral part to form meaningful number
    }

    // trim the decimal part according to precision
    const decimalPart = parts[parts.length - 1].slice(0, precision) || '0'
    newNumberString = parts[0] + separator + decimalPart
  }

  return parseLocalizedNumberAsFloat(newNumberString, separator)
}

/**
 * This is a utility function that will determine what the
 * precision of an amount is based on whether it has any
 * decimal numbers.

 * @param {string} amount the localized amount (i.e. "1,23")
 * @param {string} separator the decimal separator ("." or ",")
 * @return {number} the precision of the given amount (i.e. 2)
 */
function getPrecisionFromAmount(amount: string, separator: string) {
  const maxPrecision = 2
  const [, decimals = ''] = amount.split(separator)
  return Math.min(decimals.length, maxPrecision)
}

/*******************************************
 * PUBLIC API
 *******************************************/

/**
 * calculates Font Size for unformatted amout value
 * @param value - unformatted amount value
 * @param currencyCode - the currency code i.e. 'USD'
 * @param currencyDecimal the decimal separator i.e. '.' for USD
 * @param currencySymbol - the currency Symbol i.e. '$' for USD
 * @returns {*|number}
 */
export function calculateFontSizeFromUnformattedValue(
  value: string,
  currencyCode: CurrencyCode,
  currencyDecimal: CurrencySeparator,
  currencySymbol: string
): number {
  // non-decimal currencies should have the digits after the decimal removed
  let adjustedValue = value
  if (getCurrencyPrecision(currencyCode) === 0) {
    adjustedValue = parseFloat(value || 0).toFixed(0)
  }

  const formattedValue = formatUserCentsInput(
    currencyCode,
    currencyDecimal,
    adjustedValue
  )
  return updateFontSize(formattedValue, currencySymbol)
}
/**
 * updates Font Size on mapping digits entered length
 * @param value - formatted entered input
 * @param currencySymbol - the currency Symbol i.e. '$' for USD
 * @returns {*|number}
 */
export function updateFontSize(value: string, currencySymbol: string) {
  const digitCount = value.replace(/[\.,]/g, '').length
  const currencyCharCount = currencySymbol.length
  const fontSize =
    FONT_SIZE_MAPPING[currencyCharCount][digitCount] || DEFAULT_FONT_SIZE

  return fontSize
}

/**
 * Calculates the amount entered.
 * ex: user enters 1, value will return 0001
 * strips off leading zeroes (returning 1)
 * calculates the total number of zeroes required to be prepended
 * returns 001
 * @param currencyPrecision - returns number of digits and decimal separator i.e.  2 for USD, 0 for JPY
 * @param value - returns the entered value
 * @returns {*}
 */
export function calcNewValue(currencyPrecision: number, value: string) {
  let stringValue = value

  // strips off leading zeroes
  stringValue = stringValue.replace(/^0+/, '')

  // calculates number of zeroes required to be prepended based on value length
  stringValue = prependWithZeroes(currencyPrecision, stringValue)

  return stringValue
}

/**
 * Formats the input given the raw string value.
 * ex: raw string value returns 001 -> formats to 0.01
 * 012 -> formats to 0.12
 * 123 -> formats to 1.23
 * 1234 -> formats to 1234
 * @param currencyPrecision - number of digits after decimal separator i.e. 2 for USD, 0 for JPY
 * @param currencyDecimal - decimal separator string i.e. '.' for USD, ',' for EURO
 * @param stringVal - unformatted string value entered
 * @returns formattedValue (12.34)
 */
export function formatInput(
  currencyPrecision: number,
  currencyDecimal: CurrencySeparator,
  stringVal: string
) {
  let formattedValue
  let sliceDigitsBeforeCurrencyDecimal = ''
  let sliceDigitsAfterCurrencyDecimal = ''

  if (currencyPrecision > 0) {
    sliceDigitsBeforeCurrencyDecimal = stringVal.slice(0, stringVal.length - 2)
    sliceDigitsAfterCurrencyDecimal = stringVal.slice(stringVal.length - 2)

    formattedValue = `${sliceDigitsBeforeCurrencyDecimal}${currencyDecimal}${sliceDigitsAfterCurrencyDecimal}`
  } else {
    formattedValue = parseInt(stringVal, 10).toString()
  }

  return formattedValue
}

/**
 * Use to format user input. Combines getCurrencyPrecision, replaceNonDigits, calcNewValue, and formatInput because that's
 * used several places.
 * 'USD', '.', '1' -> '0.01'
 * 'JPY', '.', '53' -> '53'
 * 'CAD', ',', '5323' -> '53,23'
 * @param currencyCode the currency code (to get the precision)
 * @param currencyDecimal the decimal separator
 * @param userInput the user input number to format
 * @return the formatted input
 */
export function formatUserCentsInput(
  currencyCode: CurrencyCode,
  currencyDecimal: CurrencySeparator,
  userInput: string
): string {
  const currencyPrecision = getCurrencyPrecision(currencyCode)
  const digitsOnlyValue = replaceNonDigits(userInput)
  const zeroPaddedValue = calcNewValue(currencyPrecision, digitsOnlyValue)
  return formatInput(currencyPrecision, currencyDecimal, zeroPaddedValue)
}

/**
 * Computes the required width size for amount field expansion
 * @param elem - returns document element that is to be manipulated (amount field)
 * @param text - returns the inner HTML
 * @returns {*|number}
 */
export function measureTextSize(
  elem: HTMLElement,
  text: string = elem.innerHTML
) {
  const { fontSize } = window.getComputedStyle(elem, null)
  return measureTextSizeWithStyle(fontSize, elem, text)
}

/**
 * Computes the required width size for amount field expansion, but does not require a node, just the fontSize and fontFamily
 * @param {String|number} fontSize - the font size to measure against (number will be treated as px)
 * @param {HTMLElement} element - the element that is going to be changed , used to grab font-family and font-weight for accurate measurement
 * @param {String} text - the string to measure
 * @return {number} the width of the text
 */
export function measureTextSizeWithStyle(
  fontSize: number,
  element: Object,
  text: string
): number {
  element = element || {} // handle `null` as well as `undefined`
  const spanElem = document.createElement('span')

  // get the computed styles from the element and grab the font-family and font-weight
  // for accurate measurements, with sensible defaults
  const elementStyles =
    (element.nodeType && window.getComputedStyle(element, null)) || {}
  const {
    fontFamily = "'PayPal-Sans-Big', sans-serif",
    fontWeight = '300',
  } = elementStyles

  spanElem.style.fontSize =
    typeof fontSize === 'number' ? `${fontSize}px` : fontSize
  spanElem.style.fontFamily = fontFamily
  spanElem.style.fontWeight = fontWeight
  spanElem.style.position = 'fixed'
  spanElem.style.top = '-1000px'
  spanElem.style.left = '-1000px'
  spanElem.innerHTML = text
  appendChild(spanElem)
  const width = spanElem.clientWidth
  removeChild(spanElem)
  return width || 0
}

/**
 * Expands the textbox width based on the size/length of the amount
 * @param textbox - element for amount field
 * @returns {*|number}
 */
export function autoExpandTextBox(textbox: HTMLTextAreaElement) {
  const style = textbox.style
  style.width = '0'

  const desiredW = measureTextSize(textbox, textbox.value)

  style.textAlign = 'center'
  style.width = `${desiredW}px`

  return desiredW
}

/**
 *  Takes the entered value in the amount field
 *  @param string - value entered in the amount field
 *  @returns number - removes any non digits character and only returns numeric entry.
 */
export function replaceNonDigits(value: string) {
  return (value && value.replace(/[\.,٫]/g, '')) || ''
}

/**
 * Normalizes number inputs into english numericals to support other country symbols
 *
 * @param {string} - value entered in the amount field
 * @returns {string} - newString after normalizing number symbols found
 */
export function normalizeNumberInputs(value: string) {
  const NUMBER_MAPPING = { ...JAPAN_NUMBER_MAPPING }
  let newString = ''

  for (let i = 0; i < value.length; i++) {
    newString += _.get(NUMBER_MAPPING, value[i], value[i])
  }

  return newString
}

/**
 * Return a function that will format function for input
 * fields with a pre-selected decimal separator.
 * @param {string} decimalSeparator - i.e. "."
 * @returns {Function} amountToProperCurrencyPrecision(currencyCode, amount)
 */
export function getLocalizedNumberFormatter(
  decimalSeparator: CurrencySeparator
) {
  return (currencyCode: CurrencyCode, amount: string) =>
    formatLocalizedNumber(currencyCode, amount, decimalSeparator)
}

/**
 * Takes a decimal separator and returns a curried function
 * that will format localized numbers into their US equivalents,
 * with some special logic to handle NaN scenarios.
 *
 * @param {strint} decimalSeparator
 * @return {function} formatting function
 */
export const getUSNumberFormatter = (decimalSeparator: CurrencySeparator) => (
  localizedAmount: string
) => {
  const localizedNumber = parseLocalizedNumberAsFloat(
    localizedAmount,
    decimalSeparator
  )
  const stringNumber = localizedNumber.toString()

  // TODO: clean up shouldFetchNewAmount function to not need these exceptions
  if (stringNumber !== 'NaN') {
    return stringNumber
  }
}

/**
 * Formats a localized number taking into account non-decimal
 * currencies and allowing for partial amounts such as "23."
 *
 * This function filters the input allowed into the XB amount fields.
 *
 * @param {string} currencyCode - The currency code such as ("EUR", "JPY", etc.)
 * @param {string} amount - The localized number as typed in by the user ("1,23")
 * @param {string} separator - The decimal separator ("." or ",")
 *
 * @returns the formatted amount
 */
export function formatLocalizedNumber(
  currencyCode: CurrencyCode,
  amount: string,
  separator: CurrencySeparator = '.'
) {
  // remove anything that's not a digit or the separator
  amount = stripInvalid(String(amount), separator)

  if (amount === '') {
    // empty amounts are fine, and we just return that
    return ''
  }

  // amounts that are equal to the separator are fine
  if (amount === separator) {
    return amount
  }

  const precision = getCurrencyPrecision(currencyCode)
  if (precision === 0) {
    // we're not passing any precision to roundDownWithPrecision
    // to convert it to the lowest integer. This applies in the
    // cases of JPY and TWD.
    // 0.8 -> 0
    return roundDownWithPrecision(separator, amount).toString()
  }

  // 42.345.53 should be turned into 42.34
  // a more common scenario is someone mistakenly adding another `.`
  // we can't just use parseFloat(amount).toString() here because
  // if the user has typed "42." that will lose the trailing `.`
  const dotSplitAmount = amount.split(separator)
  if (dotSplitAmount.length > 2) {
    // get rid of anything after the second separator
    amount = dotSplitAmount.slice(0, 2).join(separator)
  }

  // "42." has no precision and can just be returned
  // we want to keep the `.` so this allows people to add precision
  // because this is called while they're typing
  // Note, we're using this regex rather than `endsWith` to prevent
  // the user from typing `..` for example.
  const endsWithSeparator = new RegExp(`\\d\\${separator}$`)
  if (endsWithSeparator.test(amount)) {
    return amount
  }

  // determine the current precision to avoid padding with
  // extra zeroes "43.0" or whatever is fine
  const currentPrecision = getPrecisionFromAmount(amount, separator)
  const roundedDownAmount = roundDownWithPrecision(
    separator,
    amount,
    currentPrecision
  )
  const fixedAmount = roundedDownAmount.toFixed(currentPrecision)
  const finalAmount = fixedAmount.replace('.', separator)

  // ".42" is totally valid input (given a precision of 2)
  // and if we don't do this then the value returned is "0.42"
  if (amount.startsWith(separator)) {
    return finalAmount.slice(1, finalAmount.length)
  }

  return finalAmount || ''
}

/**
 * Determines the placeholder for each country based on the locale
 * Could be "0.00", "0,00" or "0" depending on locale/currency.
 *
 * @param {string} currencyCode - The currency code to check
 * @param {string} separator - The decimal separator
 * @return {string} the localized placeholder text "0,00" "0.00" or "0"
 */
export function getPlaceholderForCurrencyCode(
  currencyCode: CurrencyCode,
  separator: CurrencySeparator = '.'
) {
  const precision = getCurrencyPrecision(currencyCode)
  return parseFloat('0')
    .toFixed(precision)
    .replace('.', separator)
}

/**
 * Determines the placeholder for each country based on the locale
 * Could be "0.00", "0,00" or "0" depending on locale/currency.
 *
 * @param {string} currencyCode - The currency code to check
 * @param {string} separator - The decimal separator
 * @return {string} the localized placeholder text "0,00" "0.00" or "0"
 */
export function getPlaceholderAmountForMGM(
  currencyCode: CurrencyCode,
  separator: CurrencySeparator = '.'
) {
  const precision = getCurrencyPrecision(currencyCode)
  return parseFloat(MGM_AMOUNT)
    .toFixed(precision)
    .replace('.', separator)
}

/**
 * Parses a localized number as a float (similar to griffin.normalize)
 * which is important for manipulating the amount in JS and for
 * passing it to our services. ("12,34" => 12.34)
 *
 * @param {string} amount Takes a localized string i.e. "12,34"
 * @param {string} separator The decimal separator i.e. "," or "."
 * @return {float} A floating point number i.e. 12.34
 */
export function parseLocalizedNumberAsFloat(
  amount: string,
  separator: CurrencySeparator
) {
  // strip any unsupported separators, etc
  const safeNumber = stripInvalid(amount, separator)

  // if us-localized we can just call parse float
  if (separator === '.') {
    return parseFloat(safeNumber)
  }

  // replace localized separator with "."
  const dottedNumber = safeNumber.replace(
    new RegExp(`\\${separator}`, 'g'),
    '.'
  )

  // remove all but last "."
  const strippedNumber = dottedNumber.replace(
    /(.*)\./g,
    (match, p1) => `${p1.replace(/\./g, '')}.`
  )

  // convert to numeric form and return
  return parseFloat(strippedNumber)
}

/**
 * Add group separator to amount displayed in input boxes
 */
export function addGroupSeparatorToAmount(
  focused: boolean,
  amount: string,
  decimalSeparator: CurrencySeparator,
  groupSeparator: CurrencySeparator,
  currencyCode: CurrencyCode
) {
  // if {amount, groupSeparator, decimalSeparator} doesn't exist or if currently typing, return the amount
  // otherwise format the amount
  if (focused || !amount || !groupSeparator || !decimalSeparator) {
    return amount
  }
  let [integral, decimal] = amount.split(decimalSeparator)
  decimal = decimal || 0
  const precision = getCurrencyPrecision(currencyCode)
  // if precision is 0, there is no decimal part; else parse decimal and set precision
  const decimalPart = precision
    ? decimalSeparator +
      Number(`.${decimal}`)
        .toFixed(precision)
        .slice(2)
    : ''
  let left,
    right,
    res = ''

  while (integral.length > 3) {
    left = integral.substring(0, integral.length - 3)
    right = integral.substring(integral.length - 3)

    integral = left
    res = groupSeparator + right + res
  }
  res = integral + res + decimalPart
  return res
}

/**
 * Validates a number by parsing it as a float and ensuring it's not 0 and not NaN
 * @param {String} amount The number to validate
 * @return {Boolean} whether it's valid
 */
export function amountIsInvalid(amount: string, decimalSeparator: string) {
  // Javascript's parseFloat doesn't take a locale parameter
  const amountNumber = parseFloat(amount.replace(decimalSeparator, '.'))
  return _.isNaN(amountNumber) || amountNumber === 0
}

/**
 * Determines whether the amount is 0
 * @param {String} amount The number to validate
 * @return {Boolean} whether it's 0
 */
export function amountIsZero(amount: string, separator: CurrencySeparator) {
  const amountAsFloat = parseLocalizedNumberAsFloat(amount, separator)
  return _.isNaN(amountAsFloat) || amountAsFloat === 0
}

/**
 * Validates that the minimum amount for donations is above or equal the minimum provided amount
 * @param {Boolean} isAmountReadonly wether the amount field is readonly
 * @param {String} amount The number to validate
 * @param {Amount} minimumAmount The minimum amount to validate agaisnt
 * @param {string} separator The decimal separator
 * @param {currencyCode} the currency code
 * @return {Boolean} whether it's valid
 */
export function amountAboveMinimumDonation(
  isAmountReadonly: boolean,
  amount: string,
  minimumAmount: Amount,
  currencyCode: CurrencyCode
) {
  // Do not enforce minimum restriction for fixed amounts
  if (isAmountReadonly) {
    return true
  }

  let minimumDonationAmount = Number(_.get(minimumAmount, 'value', '0'))

  if (currencyCode === 'USD') {
    // For US donations, set the minimum amount limit as the lager amount
    // of the client's configuration and $1
    minimumDonationAmount = Math.max(minimumDonationAmount, 1)
  } else if (currencyCode !== 'USD' && !minimumDonationAmount) {
    // if no minimum amount has been provided, validation is not performed
    return true
  }

  return parseFloat(amount) >= minimumDonationAmount
}
/**
 * Validates the maximum amount for a donation is below or equal the maximum provided amount
 * @param {Boolean} isAmountReadonly wether the amount field is readonly
 * @param {String} amount The number to validate
 * @param {Amount} maximumAmount The maximum amount to validate agaisnt
 * @param {String} separator The decimal separator
 * @param {currencyCode} currencyCode The currency code
 * @return {Boolean} wether it's valid
 */
export function amountBelowMaximumDonation(
  isAmountReadonly: boolean,
  amount: string,
  maximumAmount: Amount,
  currencyCode: CurrencyCode
) {
  // Do not enforce maximum restriction for fixed amounts
  if (isAmountReadonly) {
    return true
  }

  let maximumDonationAmount = _.get(maximumAmount, 'value')

  maximumDonationAmount = maximumDonationAmount
    ? Number(maximumDonationAmount)
    : null

  if (!maximumDonationAmount) {
    // If no maximum donation amount is provided, no validation is performed.
    return true
  }

  return parseFloat(amount) <= maximumDonationAmount
}
